import { counting, isNumber, sort } from 'radash';
import { Inductance } from 'src/models/validators/fields/Inductance';

import {
    AcGroundingConfiguration,
    AcGroundingConfigurationEnum,
    BatteryTechnologies,
    type BreakerPortSide,
    BreakerTypes,
    Cable,
    Compliance,
    Compliances,
    currentConverter,
    DcGroundingConfiguration,
    DcGroundingConfigurationEnum,
    energyConverter,
    getBreakerMaxVoltage,
    getConverterByUnit,
    inductanceConverter,
    LengthConverter,
    MeasurementSystem,
    OpeningTime,
    PortControlMethods,
    PowerFlowDirection,
    powerConverter,
    Resistance,
    resistanceConverter,
    TemperatureConverter,
    TVoltageUnit,
    timeConverter,
    UnitConverter,
    Voltage,
    voltageConverter,
    WireSize,
} from 'models';
import { round } from 'helpers/round';

import { digitalConverter } from '../../models/units/digital';

type MeasurementWithoutUnit = {
    value?: number;
    min?: number;
    nom?: number;
    max?: number;
};

type Measurement<TUnit extends string> = MeasurementWithoutUnit & {
    unit: TUnit;
};

const GROUNDING_CONFIGURATION_LABELS: Record<string, string> = Object.fromEntries(
    [...AcGroundingConfiguration.options, ...DcGroundingConfiguration.options].map(({ value, label }) => [
        value,
        label,
    ]),
);

const UL_GROUNDING_LABELS: Record<string, string> = {
    [AcGroundingConfigurationEnum.TN_S]: 'Solidly grounded (TN-S)',
    [AcGroundingConfigurationEnum.IT]: 'High-resistance grounded (IT)',
    [DcGroundingConfigurationEnum.SYMMETRICAL_IMPEDANCE]: 'Symmetrical impedance grounded',
};

const IEC_GROUNDING_LABELS: Record<string, string> = {
    [AcGroundingConfigurationEnum.UNEARTHED]: 'Unearthed',
};

const BreakerTypeLabels: Record<BreakerTypes, string> = {
    [BreakerTypes.MCB]: 'MCB',
    [BreakerTypes.MCCB]: 'MCCB',
    [BreakerTypes.SSCB]: 'SSCB',
    [BreakerTypes.EFUSE]: 'E-Fuse',
    [BreakerTypes.ACB]: 'ACB',
};

const FormatHelpers = {
    formatVolts: (value?: number | null) => {
        if (!isNumber(value)) {
            return '';
        }

        return `${value}\u00a0V`;
    },

    round: (number: number | null | undefined, precision = 0) => {
        if (number === null || number === undefined) {
            return 0;
        }

        return round(number, precision);
    },

    isEmpty: (measurement: MeasurementWithoutUnit) => {
        let total = 0;

        ['min', 'nom', 'max', 'value'].forEach((field) => {
            if (field in measurement && measurement[field as keyof typeof measurement]) {
                total += 1;
            }
        });

        return !!total;
    },

    formatMeasurement: <TUnit extends string, TBaseUnit extends TUnit>(
        toFormat: { value: number | null; unit: TUnit } | null,
        converter: UnitConverter<TUnit, TBaseUnit>,
    ) => {
        const { value, unit: from } = toFormat || {};

        let string = '';

        if (isNumber(value) && from) {
            const best = converter.toBest({ value, from });
            const rounded = round(best.value, 1);

            string = `${rounded.toLocaleString()}\u00a0${rounded === 0 ? (converter.defaultViewUnit ?? converter.baseUnit) : best.unit}`;
        }

        string = string.replaceAll('ohm', 'Ω');

        return string;
    },

    formatValue: <TUnit extends string, TBaseUnit extends TUnit>(
        value: number | null | undefined | { value: number | null },
        converter: UnitConverter<TUnit, TBaseUnit>,
        unit = converter.baseUnit,
    ): string => {
        if (value && typeof value === 'object') {
            return FormatHelpers.formatValue(value.value, converter, unit);
        }

        if (!isNumber(value)) {
            return '';
        }

        return FormatHelpers.formatMeasurement({ value, unit }, converter);
    },

    transformValue: <TUnit extends string, TBaseUnit extends TUnit>(
        value: number | null | undefined,
        converter: UnitConverter<TUnit, TBaseUnit>,
        from: TUnit,
        to: TUnit,
    ) => {
        if (!isNumber(value)) {
            return undefined;
        }

        if (value === undefined) return undefined;

        const convertedValue = converter.convert({
            value: +value,
            from,
            to,
        });

        return Number(convertedValue.value.toFixed(2));
    },

    transformToBestUnit: <TUnit extends string, TBaseUnit extends TUnit>(
        values: Measurement<TUnit>,
        converter: UnitConverter<TUnit, TBaseUnit>,
    ): Measurement<TUnit> => {
        const { min, nom, max, unit: baseUnit } = values;

        const valueToBaseUpon = nom || min || max;

        if (valueToBaseUpon === undefined) {
            return values;
        }

        const best = converter.toBest({
            value: +valueToBaseUpon,
            from: baseUnit,
        });

        if (!best) {
            return values;
        }

        const bestUnit = best.unit;

        const format = (value?: number) => {
            if (value === undefined) return undefined;

            const convertedValue = converter.convert({
                value: +value,
                from: baseUnit,
                to: bestUnit,
            });

            return Number(convertedValue.value.toFixed(2));
        };

        return {
            unit: bestUnit,
            min: format(min),
            nom: format(nom),
            max: format(max),
        };
    },

    formatMinNomMax: <TUnit extends string, TBaseUnit extends TUnit>(
        values: any = {},
        converter: UnitConverter<TUnit, TBaseUnit>,
    ): string => {
        const { min, nom, max, unit } = values ?? {};
        const hasMin = isNumber(min);
        const hasNom = isNumber(nom);
        const hasMax = isNumber(max);

        if (!unit) return '';

        const { nom: nomConverted, unit: bestNomUnit } = FormatHelpers.transformToBestUnit({ nom, unit }, converter);

        if (hasMin && hasNom && hasMax) {
            const {
                min: minConverted,
                max: maxConverted,
                unit: bestMinMaxUnit,
            } = FormatHelpers.transformToBestUnit({ min, max, unit }, converter);

            return `${nomConverted}\u00a0${bestNomUnit} (${minConverted}-${maxConverted}\u00a0${bestMinMaxUnit})`;
        }

        if (hasMin && hasNom) {
            const { min: minConverted, unit: bestMinUnit } = FormatHelpers.transformToBestUnit(
                { min, unit },
                converter,
            );

            return `${nomConverted} ${bestNomUnit} (${minConverted} ${bestMinUnit} min)`;
        }

        if (hasMin && hasMax) {
            const {
                min: minConverted,
                max: maxConverted,
                unit: bestMinMaxUnit,
            } = FormatHelpers.transformToBestUnit({ min, max, unit }, converter);

            return `${minConverted}-${maxConverted} ${bestMinMaxUnit}`;
        }

        if (hasNom && hasMax) {
            const { max: maxConverted, unit: bestMaxUnit } = FormatHelpers.transformToBestUnit(
                { max, unit },
                converter,
            );

            return `${nomConverted} ${bestNomUnit} (${maxConverted} ${bestMaxUnit} max)`;
        }

        if (hasMin) {
            const { min: minConverted, unit: bestMinUnit } = FormatHelpers.transformToBestUnit(
                { min, unit },
                converter,
            );

            return `${minConverted} ${bestMinUnit} min`;
        }

        if (hasNom) {
            return `${nomConverted} ${bestNomUnit}`;
        }

        if (hasMax) {
            const { max: maxConverted, unit: bestMaxUnit } = FormatHelpers.transformToBestUnit(
                { max, unit },
                converter,
            );

            return `${maxConverted} ${bestMaxUnit} max`;
        }

        return '';
    },

    formatCurrent: (values: any = {}) => {
        if (typeof values === 'number') {
            return FormatHelpers.formatValue(values, currentConverter, 'A');
        }

        return 'value' in values
            ? FormatHelpers.formatValue(values.value, currentConverter, 'A')
            : FormatHelpers.formatMinNomMax(values, currentConverter);
    },

    formatVoltage: (values: any = {}) => {
        if (typeof values === 'number') {
            return FormatHelpers.formatValue(values, voltageConverter, 'V');
        }

        return 'value' in values
            ? FormatHelpers.formatValue(values.value, voltageConverter, 'V')
            : FormatHelpers.formatMinNomMax(values, voltageConverter);
    },

    formatPower: (values: any = {}) => {
        if (typeof values === 'number') {
            return FormatHelpers.formatValue(values, powerConverter, 'W');
        }

        return 'value' in values
            ? FormatHelpers.formatValue(values.value, powerConverter, 'W')
            : FormatHelpers.formatMinNomMax(values, powerConverter);
    },

    formatCapacity: (values: any = {}) => {
        if (typeof values === 'number') {
            return FormatHelpers.formatValue(values, energyConverter, 'Wh');
        }

        return 'value' in values
            ? FormatHelpers.formatValue(values.value, energyConverter, values.unit ?? 'Wh')
            : FormatHelpers.formatMinNomMax(values, energyConverter);
    },

    formatPoles: (poles: number): string => `${poles}P`,

    formatBreakerType: (breakerType: BreakerTypes): string => BreakerTypeLabels[breakerType] ?? breakerType,

    formatOpeningTime: (value: OpeningTime): string => {
        if (value === 'ultra-fast') return 'Ultra-fast';
        if (value === 'fast') return 'Fast';
        return 'Conventional';
    },

    formatPhase: (phase: number | string) => `${phase} Phase`,

    formatBatteryTechnology: (value: string): string =>
        BatteryTechnologies.options.find((option) => option.value === value)?.label ?? value,

    formatLength: (value: number | null, measurementSystem: MeasurementSystem = MeasurementSystem.METRIC) => {
        return FormatHelpers.formatValue(value, new LengthConverter(measurementSystem), 'm');
    },

    formatPercentage: (value: number) => {
        return `${Math.round(value * 10000) / 100}%`;
    },

    formatOption: (options: any[], value: any) => {
        if (!value) {
            return '';
        }

        const option = options.find((option) => option.value === value);

        return option?.label || '';
    },

    formatCompliance: (compliance: Compliance) => {
        return FormatHelpers.formatOption(Compliances.options, compliance);
    },

    formatPowerFlowDirection: (powerFlowDirection: string, displayAsSinglePort = false) => {
        if (displayAsSinglePort && (powerFlowDirection === 'input' || powerFlowDirection === 'output')) {
            return 'Unidirectional';
        }

        return FormatHelpers.formatOption(PowerFlowDirection.options, powerFlowDirection);
    },

    formatBreakerCardVoltage: (side: {
        poles?: string | null;
        voltagePerPole?: { value?: number | null; unit?: string } | null;
        maxVoltage?: { value?: number | null; unit?: string } | null;
        voltage?: { nom?: number | null; max?: number | null; unit?: string } | null;
    }) => {
        const voltagePerPole = FormatHelpers.formatVolts(side.voltagePerPole?.value);
        const max = side.maxVoltage ?? getBreakerMaxVoltage(side as BreakerPortSide);
        const maxVoltage = FormatHelpers.formatVolts(max?.value);

        if (maxVoltage) {
            return `${voltagePerPole || '-'} / ${maxVoltage}`;
        }

        return voltagePerPole || FormatHelpers.formatMinNomMax(side.voltage, voltageConverter);
    },

    formatWireSize: (wireSize: WireSize | null, measurementSystem: MeasurementSystem | null = null) => {
        const option = WireSize.options.find((option) => option.value === wireSize);

        if (!option) {
            return '';
        }

        if (measurementSystem === MeasurementSystem.METRIC) {
            // @ts-ignore
            return option.mm2 + ' mm²';
        }

        if (measurementSystem === MeasurementSystem.IMPERIAL) {
            // @ts-ignore
            return option.awg + ' AWG';
        }

        return option.label;
    },

    formatResistance: (resistance?: Resistance | { value?: number | null; unit?: string } | number | null) => {
        if (typeof resistance === 'number') {
            return FormatHelpers.formatValue(resistance, resistanceConverter, 'ohm').replace(/ohm/g, 'Ω');
        }

        // Non-range resistances (droop resistance, earthing impedance) keep the legacy `{value}` shape.
        if (resistance && 'value' in resistance && !('nom' in resistance)) {
            return FormatHelpers.formatValue(resistance.value, resistanceConverter, 'ohm').replace(/ohm/g, 'Ω');
        }

        return FormatHelpers.formatMinNomMax(resistance ?? {}, resistanceConverter).replace(/ohm/g, 'Ω');
    },

    formatInductance: (inductance?: Inductance | number | null) => {
        if (typeof inductance === 'number') {
            return FormatHelpers.formatValue(inductance, inductanceConverter, 'H');
        }

        return FormatHelpers.formatMinNomMax(inductance ?? {}, inductanceConverter);
    },

    formatOperatingVoltage: (operatingVoltage: Cable['electrical']['operatingVoltage']): string | null => {
        if (!operatingVoltage?.betweenLines?.value) {
            return null;
        }

        const betweenLinesVoltage = FormatHelpers.formatValue(
            operatingVoltage.betweenLines.value,
            voltageConverter,
            'V',
        );
        const betweenLinesVoltageUnit = betweenLinesVoltage.split(' ')[1] as TVoltageUnit;
        const toEarthVoltage = betweenLinesVoltageUnit
            ? FormatHelpers.transformValue(
                  operatingVoltage.toEarth.value,
                  voltageConverter,
                  'V',
                  betweenLinesVoltageUnit,
              )
            : null;

        const formattedOperatingVoltage =
            toEarthVoltage && betweenLinesVoltage ? `${toEarthVoltage} / ${betweenLinesVoltage}` : null;
        return formattedOperatingVoltage;
    },

    formatControlMethod: (controlMethod: string | null) => {
        return FormatHelpers.formatOption(PortControlMethods.options, controlMethod);
    },

    formatIsolation: (isolated?: boolean) => {
        return isolated ? 'Isolated' : 'Not Isolated';
    },

    formatGroundingConfiguration: (
        configuration?: string | null,
        measurementSystem: MeasurementSystem = MeasurementSystem.METRIC,
    ): string => {
        if (!configuration) {
            return '';
        }

        const terminology =
            measurementSystem === MeasurementSystem.IMPERIAL ? UL_GROUNDING_LABELS : IEC_GROUNDING_LABELS;

        return terminology[configuration] ?? GROUNDING_CONFIGURATION_LABELS[configuration] ?? configuration;
    },

    formatDimensions: <MSystem extends MeasurementSystem>(values: any, converter: LengthConverter<MSystem>) => {
        const { width, length, height, value, unit } = values;

        if (!unit) return '';

        if (isNumber(value)) {
            return FormatHelpers.formatValue(value, converter, unit);
        }

        const minValue = [width, length, height].reduce((min, next) => Math.min(next, min));

        const smallestUnit = converter.toBest({ value: minValue, from: unit }).unit;

        const formatted = [];

        const formattedWidth = FormatHelpers.transformValue(width, converter, unit, smallestUnit);
        const formattedLength = FormatHelpers.transformValue(length, converter, unit, smallestUnit);
        const formattedHeight = FormatHelpers.transformValue(height, converter, unit, smallestUnit);

        if (formattedLength) {
            formatted.push(`L: ${formattedLength} ${smallestUnit}`);
        }

        if (formattedWidth) {
            formatted.push(`W: ${formattedWidth} ${smallestUnit}`);
        }

        if (formattedHeight) {
            formatted.push(`H: ${formattedHeight} ${smallestUnit}`);
        }

        return formatted.join(' × ');
    },

    getUniqueVoltages: (dirtyVoltages: Voltage[]) => {
        const voltages = dirtyVoltages.filter(
            (voltage) => voltage && voltage.unit && (voltage.min || voltage.nom || voltage.max),
        );

        const parseOptionalNumber = (value: string | undefined) =>
            value === undefined || value === '' ? null : +value;

        const delimiter = '|';
        const stringify = (voltage: Voltage) =>
            [voltage.min || '', voltage.nom || '', voltage.max || '', voltage.unit].join(delimiter);
        const parse = (voltageString: string) => {
            const [min, nom, max, unit] = voltageString.split(delimiter);

            return {
                min: parseOptionalNumber(min),
                nom: parseOptionalNumber(nom),
                max: parseOptionalNumber(max),
                unit: unit ?? 'V',
            } as Voltage;
        };

        const voltagesCount = counting(voltages, stringify);
        const voltagesCountAsArray = Object.entries(voltagesCount).map(([voltage, count]) => ({ voltage, count }));
        const voltagesCountAsSortedArray = sort(voltagesCountAsArray, ({ count }) => count, true);
        const uniqueVoltages = voltagesCountAsSortedArray.map(({ voltage }) => parse(voltage));

        return uniqueVoltages;
    },

    formatTime: (value: number | { value: number }) => {
        return FormatHelpers.formatValue(value, timeConverter);
    },

    // TODO FIX THIS SHIZZLE..
    formatTemperature: ({ value }: any) => {
        if (value === 'null' || value === undefined) {
            return '';
        }

        return FormatHelpers.formatValue(value, new TemperatureConverter(MeasurementSystem.METRIC), 'K');
    },

    formatTemperatureRange: <MSystem extends MeasurementSystem>(
        { min, max, unit }: { min?: number; max?: number; unit?: TemperatureConverter<MSystem>['units'][number] },
        converter: TemperatureConverter<MSystem>,
    ) => {
        const {
            min: minConverted,
            max: maxConverted,
            unit: bestMinMaxUnit,
        } = FormatHelpers.transformToBestUnit({ min, max, unit: unit ?? converter.baseUnit }, converter);

        const hasMin = isNumber(min);
        const hasMax = isNumber(max);

        if (!hasMin && !hasMax) {
            return '';
        }

        if (hasMin && hasMax) {
            return `${minConverted} to ${maxConverted} °${bestMinMaxUnit}`;
        }

        if (hasMin) {
            return `${minConverted} °${bestMinMaxUnit} min`;
        }

        if (hasMax) {
            return `${maxConverted} °${bestMinMaxUnit} max`;
        }
    },

    formatCorrectionFactor: (value?: number | null) => {
        if (typeof value === 'number') {
            return Math.round(value * 100) / 100;
        }

        return 'N/A';
    },

    formatThermalResistivity: (value?: any) => {
        return `${value?.value} Km / W`;
    },

    formatFileSize: (size: number) => {
        return FormatHelpers.transformToBestUnit(
            {
                value: size,
                unit: 'B',
            },
            digitalConverter,
        );
    },

    getValueWithConverter: (value: any, converter: any) => {
        if (value.unit === 'K' && ('min' in value || 'max' in value)) {
            return FormatHelpers.formatTemperatureRange(value, converter);
        }

        if (Object.keys(value).some((key) => ['min', 'nom', 'max'].includes(key))) {
            return FormatHelpers.formatMinNomMax(value, converter);
        }

        if (Object.keys(value).some((key) => ['length', 'width', 'height'].includes(key))) {
            return FormatHelpers.formatDimensions(value, converter);
        }

        if ('value' in value && value.unit) {
            return FormatHelpers.formatValue(value.value, converter, value.unit);
        }

        return null;
    },

    getFormattedValue: (value: any, measurementSystem: MeasurementSystem) => {
        const converter = value?.unit && getConverterByUnit(value.unit, measurementSystem);

        if (converter) {
            return FormatHelpers.getValueWithConverter(value, converter);
        }

        return null;
    },

    formatCount: (count: number, singular: string, plural: string) => {
        if (count === 0) {
            return `No ${plural}`;
        }

        return `${count} ${count === 1 ? singular : plural}`;
    },
};

export { FormatHelpers };
